export const PRODUCT_GALLERY_LIMIT = 8;
export const PRODUCT_VIDEO_LIMIT = 1;

// Only absolute http(s) values are safe to hand to <img src>. A bare storage
// path resolves against the PAGE url and renders as a broken image. The stack
// admits these exist: catalogImport writes a field named `image_path`, the
// admin console guards it with isUrl(), and both branches of
// catalog_products_public filter on `^https?://` — but nothing filters
// gallery_images anywhere, and seed_supplier_from_factory copies image/gallery
// into products.* without a filter.
const isHttpUrl = (u) => typeof u === 'string' && /^https?:\/\//i.test(u.trim());

// EDITOR view of a product's images: every stored value, in stored order.
// The supplier dashboard indexes into this list to delete an image, so it must
// stay 1:1 with what is in the row — a value that will not render still has to
// be visible and removable. Buyer-facing pages want getProductDisplayImages().
export function getProductGalleryImages(product) {
  const gallery = Array.isArray(product?.gallery_images) ? product.gallery_images : [];
  const legacy = product?.image_url ? [product.image_url] : [];
  return [...new Set([...gallery, ...legacy].filter(Boolean))].slice(0, PRODUCT_GALLERY_LIMIT);
}

// BUYER view: primary image first, then the extras, and only values that can
// actually load. Primary-first matches getFactoryProductImages so the two
// product pages agree on which shot is the hero — the editor order puts
// image_url last, and seed_supplier_from_factory maps the factory's curated
// `image` into products.image_url, so a seeded product would otherwise show an
// arbitrary extra shot as its main preview.
export function getProductDisplayImages(product) {
  const gallery = Array.isArray(product?.gallery_images) ? product.gallery_images : [];
  const primary = product?.image_url ? [product.image_url] : [];
  return [...new Set([...primary, ...gallery].filter(isHttpUrl))].slice(0, PRODUCT_GALLERY_LIMIT);
}

export function getPrimaryProductImage(product) {
  return getProductGalleryImages(product)[0] || null;
}

// Factory catalog products use `image` (primary) + `gallery_images` (extras),
// unlike transactional products (`image_url`). Primary first, then extras; same
// dedup + cap so the factory product page matches the product page's gallery.
export function getFactoryProductImages(product) {
  const gallery = Array.isArray(product?.gallery_images) ? product.gallery_images : [];
  const primary = product?.image ? [product.image] : [];
  return [...new Set([...primary, ...gallery].filter(Boolean))].slice(0, PRODUCT_GALLERY_LIMIT);
}

export function normalizeProductDraftMedia(product) {
  const galleryImages = [...new Set((product?.gallery_images || []).filter(Boolean))].slice(0, PRODUCT_GALLERY_LIMIT);
  return {
    ...product,
    gallery_images: galleryImages,
    image_url: galleryImages[0] || product?.image_url || null,
    video_url: product?.video_url || null,
  };
}

function localizeSpecLabel(key, lang = 'en') {
  const labels = {
    spec_material: {
      ar: 'الخامة / المادة',
      zh: '材质',
      en: 'Material',
    },
    spec_dimensions: {
      ar: 'الأبعاد / المقاس',
      zh: '尺寸 / 规格',
      en: 'Dimensions',
    },
    spec_unit_weight: {
      ar: 'وزن القطعة',
      zh: '单件重量',
      en: 'Unit Weight',
    },
    spec_color_options: {
      ar: 'الألوان / الخيارات',
      zh: '颜色 / 款式',
      en: 'Colors / Variants',
    },
    spec_packaging_details: {
      ar: 'تفاصيل التغليف',
      zh: '包装信息',
      en: 'Packaging',
    },
    spec_customization: {
      ar: 'التخصيص / OEM',
      zh: '定制 / OEM',
      en: 'Customization / OEM',
    },
    spec_lead_time_days: {
      ar: 'مدة التجهيز',
      zh: '交期（天）',
      en: 'Lead Time (days)',
    },
  };

  return labels[key]?.[lang] || labels[key]?.en || key;
}

function localizeSpecValue(key, value, lang = 'en') {
  if (value === null || value === undefined) return value;
  let text = String(value).trim();
  if (!text) return text;
  if (lang === 'ar') {
    const canned = {
      China: 'الصين',
      'Standard export dimensions available': 'أبعاد تصديرية قياسية متاحة',
      'Standard export assortment': 'تشكيلة تصديرية قياسية',
      'Master carton export packing with inner protection and barcode-ready labels.': 'تغليف كرتوني تصديري مع حماية داخلية وملصقات جاهزة للباركود.',
      'Private label packaging, custom firmware presets, logo printing, and bundled accessory kits.': 'تغليف علامة خاصة، وإعدادات مخصصة للبرمجة، وطباعة الشعار، مع أطقم ملحقات مرفقة.',
      'ABS / steel / engineered components': 'ABS / فولاذ / مكونات هندسية',
    };
    if (canned[text]) return canned[text];
    if (key === 'spec_unit_weight') {
      const weightMatch = text.match(/^(?:kg\s*)?(\d+(?:\.\d+)?)/i) || text.match(/(\d+(?:\.\d+)?)\s*kg/i);
      if (weightMatch) {
        return `${Number(weightMatch[1]).toFixed(2).replace(/\.00$/, '')} كجم`;
      }
    }
    if (key === 'spec_lead_time_days' && /^\d+$/.test(text)) {
      return `${text} يوم`;
    }
    text = text
      .replace(/\bChina\b/g, 'الصين')
      .replace(/\bMOQ\b/g, 'الحد الأدنى للطلب')
      .replace(/\bOEM\b/g, 'OEM')
      .replace(/\bPrivate label\b/gi, 'علامة خاصة')
      .replace(/\bcustom packaging\b/gi, 'تغليف مخصص')
      .replace(/\blogo printing\b/gi, 'طباعة الشعار');
  }
  return text;
}

export function normalizeProductAttributes(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(attr => attr && typeof attr === 'object')
    .map(attr => ({
      name: String(attr.name || '').trim(),
      values: Array.isArray(attr.values)
        ? attr.values.map(v => String(v || '').trim()).filter(Boolean)
        : [],
    }))
    .filter(attr => attr.name && attr.values.length > 0)
    .slice(0, 20);
}

export function buildProductSpecs(product, lang = 'en') {
  const fields = [
    'spec_material',
    'spec_dimensions',
    'spec_unit_weight',
    'spec_color_options',
    'spec_packaging_details',
    'spec_customization',
    'spec_lead_time_days',
  ];

  return fields
    .map((key) => ({
      key,
      label: localizeSpecLabel(key, lang),
      value: localizeSpecValue(key, product?.[key], lang),
    }))
    .filter(item => item.value !== null && item.value !== undefined && String(item.value).trim() !== '');
}
